<script setup lang="ts">
import { computed } from 'vue'

import InstagramIcon from '@/assets/icons/instagram.svg?component'
import LinkIcon from '@/assets/icons/link.svg?component'
import SnapchatIcon from '@/assets/icons/snapchat.svg?component'
import TikTokIcon from '@/assets/icons/tiktok.svg?component'
import { combineTrafficSourceCosts } from '@/lib/adapters/traffic-source-costs.adapter'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import type { TrafficSourceCostsType } from '@/lib/types/traffic-source-costs.types'
import { FlexComponent, TypographyComponent } from '@/ui-library'

const sourceImagesMap = {
  0: LinkIcon,
  1: InstagramIcon,
  2: TikTokIcon,
  3: SnapchatIcon,
}
const props = withDefaults(
  defineProps<{
    currency: string
    groupedTrafficSourceCosts: TrafficSourceCostsType[]
    hasBorder?: boolean
    hideLabels?: boolean
    wrap?: string
    justifyContent?: string
  }>(),
  {
    hasBorder: false,
    hideLabels: false,
    justifyContent: 'start',
    wrap: 'wrap',
    groupedTrafficSourceCosts: () => [],
  },
)
const groups = computed(() => combineTrafficSourceCosts(props.groupedTrafficSourceCosts))
const formatCpc = (amount: number | string | null | undefined) =>
  formatMoneyForAdmin({ amount: Number(amount ?? 0), currency: props.currency }, { allowZero: true })
const cpcGroup = computed(() => groups.value.filter((item) => Number(item.cpc?.amount ?? 0) > 0 && item.sourceIndexes.length > 0))
const cpaGroup = computed(() => groups.value.filter((item) => Number(item.rateCpa) > 0 && item.sourceIndexes.length > 0))
</script>
<template>
  <FlexComponent :gap="8" :wrap="wrap" :justify="justifyContent">
    <FlexComponent v-if="cpcGroup.length" :class="{ 'cost-data-row__group-container': hasBorder }" :gap="4" align="center" wrap="nowrap">
      <TypographyComponent v-if="!hideLabels" variant="label-medium"> CPC: </TypographyComponent>
      <FlexComponent v-for="(item, index) in cpcGroup" :key="index" :gap="4" align="center" wrap="nowrap">
        <FlexComponent v-for="source in item.sourceIndexes" :key="source" :gap="2">
          <el-icon :size="16">
            <component :is="sourceImagesMap[source as 0 | 1 | 2 | 3]" />
          </el-icon>
        </FlexComponent>
        <TypographyComponent variant="label-medium" text-truncate>
          {{ formatCpc(item.cpc?.amount) }}
        </TypographyComponent>
      </FlexComponent>
    </FlexComponent>
    <FlexComponent v-if="cpaGroup.length" :class="{ 'cost-data-row__group-container': hasBorder }" :gap="4" align="center" wrap="nowrap">
      <TypographyComponent v-if="!hideLabels" variant="label-medium"> CPA: </TypographyComponent>
      <FlexComponent v-for="(item, index) in cpaGroup" :key="index" :gap="4" align="center" wrap="nowrap">
        <FlexComponent v-for="source in item.sourceIndexes" :key="source" :gap="2">
          <el-icon :size="16">
            <component :is="sourceImagesMap[source as 0 | 1 | 2 | 3]" />
          </el-icon>
        </FlexComponent>
        <TypographyComponent variant="label-medium">
          {{ `${item.rateCpa}%` }}
        </TypographyComponent>
      </FlexComponent>
    </FlexComponent>
  </FlexComponent>
</template>
<style lang="scss" scoped>
.cost-data-row__group-container {
  padding: 7px 8px;
  border-radius: 8px;
  border: 1px solid var(--secondary-8);
  background: var(--inverted);
}
</style>
